import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Popover } from 'antd';
import { useHiddenColumns } from './useHiddenColumns';
import './DataTable.css';

/**
 * Flow's tables, all alike (the pattern of /connections "Your accounts", 2026-10-30):
 * - laid out fixed: each column keeps its width and every cell cuts what does not fit (ellipsis, the whole value on
 *   hover) instead of running into the next one; the columns without a width share what the others leave;
 * - no sideways scroll while the box is wider than the columns need (their widths added up, about 960px for the
 *   widest); narrower, the table scrolls sideways inside its box and the actions column stays in sight on the right;
 * - every data column sorts (click its header: ascending, descending; `aria-sort` says which);
 * - Columns ▾ shows or hides any column but the first, remembered in this browser under `storageKey`.
 *
 * A column: `{ key, label, value?(row), render?(row, index), title?(row), width?, minWidth?, align?, wrap?, sortable?,
 * hideable?, rowHeader?, className?, cellClassName?(row), header?, headerTitle?, clip?, sortTestId?, cellLabel? }`.
 * Each cell carries its column's label (`data-label`, for a layout that stacks the cells on a phone) unless
 * `cellLabel: false`.
 * - `value` is what the column sorts on (string, number, Date, boolean; null and undefined go last) and, without a
 *   `render`, what the cell shows; `title` (default: the value, when the cell shows it) is the hover text.
 * - `wrap: n` lets the cell take up to n lines before it is cut (default one line); `clip: false` leaves the cell as
 *   rendered (a form control, a cell that must not be cut).
 * - `sortable: false` for a column that has nothing to sort on; a column without `value` does not sort.
 * `actions` is the last column, never hidden, sticky on the right when the table scrolls; `leading` a first, narrow
 * column (a checkbox) that neither sorts nor hides.
 */

const SORT_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
// A column without a width needs at least this much before the table scrolls sideways.
const FLEX_MIN = 160;

const blank = (v) => v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));

function comparable(v) {
  if (v instanceof Date) { return v.getTime(); }
  if (typeof v === 'boolean') { return v ? 1 : 0; }
  return v;
}

/** Compares two sort values: numbers as numbers, text in natural order ignoring case, blanks always last. */
export function compareValues(a, b, dir = 'asc') {
  const x = comparable(a);
  const y = comparable(b);
  if (blank(x) && blank(y)) { return 0; }
  if (blank(x)) { return 1; }
  if (blank(y)) { return -1; }
  const order = typeof x === 'number' && typeof y === 'number' ? x - y : SORT_COLLATOR.compare(String(x), String(y));
  return dir === 'desc' ? -order : order;
}

/** `rows` sorted on `column.value` (a stable sort: equal rows keep their order); `rows` itself when nothing sorts. */
export function sortTableRows(rows, column, dir) {
  if (!column?.value) { return rows; }
  return rows
    .map((row, index) => ({ row, index, value: column.value(row) }))
    .sort((a, b) => compareValues(a.value, b.value, dir) || a.index - b.index)
    .map((entry) => entry.row);
}

/** The width the shown columns need side by side; under it the table scrolls sideways. */
export function neededWidth(columns, { actions, leading } = {}) {
  return columns.reduce((sum, c) => sum + (c.width || c.minWidth || FLEX_MIN), 0)
    + (actions ? actions.width || 96 : 0)
    + (leading ? leading.width || 40 : 0);
}

function headerText(column) { return column.label; }

export function ColumnsButton({ columns, show, toggle, label = 'Columns' }) {
  const hideable = columns.slice(1).filter((column) => column.hideable !== false);
  if (!hideable.length) { return null; }
  return (
    <Popover
      content={(
        <fieldset className="dt-columns" data-testid="dt-columns-menu">
          <legend className="dt-sr">Columns to show</legend>
          {hideable.map((column) => (
            <label className="dt-columns-item" key={column.key}>
              <input checked={show(column.key)} onChange={() => toggle(column.key)} type="checkbox" /> {headerText(column)}
            </label>
          ))}
        </fieldset>
      )}
      placement="bottomRight"
      trigger="click"
    >
      <button aria-haspopup="true" className="dt-columns-btn" type="button">{label} <span aria-hidden="true">▾</span></button>
    </Popover>
  );
}

ColumnsButton.propTypes = { columns: PropTypes.array.isRequired, label: PropTypes.string, show: PropTypes.func.isRequired, toggle: PropTypes.func.isRequired };

/** The sort and the hidden columns of a table, for a page that puts Columns ▾ in its own toolbar (`columnState`). */
export function useTableColumns(storageKey, columns) {
  return useHiddenColumns(storageKey, columns);
}

function SortableHeader({ column, sort, onSort }) {
  const sortable = column.sortable !== false && Boolean(column.value);
  const on = sortable && sort.key === column.key;
  const style = column.width ? { width: column.width } : (column.minWidth ? { minWidth: column.minWidth } : undefined);
  const cls = ['dt-th', column.align ? `is-${column.align}` : '', column.headerClassName || ''].filter(Boolean).join(' ');
  const content = column.header || column.label;
  return (
    <th
      aria-sort={sortable ? (on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
      className={cls}
      scope="col"
      style={style}
      title={column.headerTitle || (typeof content === 'string' ? content : undefined)}
    >
      {sortable ? (
        <button className="dt-sort" data-testid={column.sortTestId} onClick={() => onSort(column.key)} type="button">
          <span className="dt-sort-label">{content}</span>
          <span aria-hidden="true" className={`dt-sort-arrow${on ? ' is-on' : ''}`}>{on ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
        </button>
      ) : <span className="dt-th-label">{content}</span>}
    </th>
  );
}

SortableHeader.propTypes = { column: PropTypes.object.isRequired, onSort: PropTypes.func.isRequired, sort: PropTypes.object.isRequired };

function Cell({ column, row, index }) {
  const value = column.value ? column.value(row) : undefined;
  const shown = column.render ? column.render(row, index) : (blank(value) ? '' : String(value instanceof Date ? value.toLocaleString() : value));
  let title;
  if (column.title) { title = column.title(row) || undefined; } else if (!column.render && !blank(value)) { title = String(shown); }
  const Tag = column.rowHeader ? 'th' : 'td';
  const cls = [column.align ? `is-${column.align}` : '', column.className || '', column.cellClassName ? column.cellClassName(row) : ''].filter(Boolean).join(' ') || undefined;
  if (column.clip === false) {
    return <Tag className={cls} data-label={column.cellLabel === false ? undefined : column.label} scope={column.rowHeader ? 'row' : undefined} title={title}>{shown}</Tag>;
  }
  const lines = typeof column.wrap === 'number' ? column.wrap : (column.wrap ? 2 : 1);
  return (
    <Tag className={cls} data-label={column.cellLabel === false ? undefined : column.label} scope={column.rowHeader ? 'row' : undefined}>
      <div className={lines > 1 ? 'dt-cell is-wrap' : 'dt-cell'} style={lines > 1 ? { WebkitLineClamp: lines } : undefined} title={title}>{shown}</div>
    </Tag>
  );
}

Cell.propTypes = { column: PropTypes.object.isRequired, index: PropTypes.number.isRequired, row: PropTypes.any };

export default function DataTable({
  actions, after, ariaLabel, caption, className, columnState, columns, columnsMenu = 'auto', defaultSort, empty, footer, leading,
  minWidth, onSort, rowKey, rowProps, rows, scrollLabel, serverSort = false, sort: controlledSort, storageKey, testId, toolbar, wrapClassName,
}) {
  const ownColumns = useHiddenColumns(storageKey, columns);
  const choice = columnState || ownColumns;
  const [ownSort, setOwnSort] = useState(defaultSort || { key: null, dir: 'asc' });
  const sort = controlledSort || ownSort;
  const sortBy = (key) => {
    const next = sort.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' };
    if (onSort) { onSort(next); }
    if (!controlledSort) { setOwnSort(next); }
  };
  const shown = columns.filter((column) => choice.show(column.key));
  const sortColumn = columns.find((column) => column.key === sort.key);
  // `serverSort`: the caller sorts (the server, say) and the rows come sorted; otherwise the table sorts them, also
  // with a controlled `sort` (+ `onSort`, to share one sort between tables).
  const sorted = useMemo(
    () => (serverSort ? rows : sortTableRows(rows, sortColumn, sort.dir)),
    [serverSort, rows, sortColumn, sort.dir],
  );
  const keyOf = (row, index) => (typeof rowKey === 'function' ? rowKey(row, index) : (rowKey ? row[rowKey] : index));
  const colCount = shown.length + (actions ? 1 : 0) + (leading ? 1 : 0);
  const width = minWidth ?? neededWidth(shown, { actions, leading });
  const menu = columnsMenu === 'auto' && !columnState && columns.slice(1).filter((c) => c.hideable !== false).length >= 2
    ? <ColumnsButton columns={columns} show={choice.show} toggle={choice.toggle} />
    : null;

  return (
    <div className={['dt', wrapClassName].filter(Boolean).join(' ')}>
      {(menu || toolbar) && <div className="dt-toolbar">{toolbar}{menu}</div>}
      {/* With `scrollLabel` the scrolling box is a focusable region, so a keyboard can scroll it sideways. */}
      <div aria-label={scrollLabel} className="dt-scroll" role={scrollLabel ? 'region' : undefined} tabIndex={scrollLabel ? 0 : undefined}>
        <table aria-label={ariaLabel} className={['dt-table', className].filter(Boolean).join(' ')} data-testid={testId} style={{ minWidth: width }}>
          {caption}
          <thead>
            <tr>
              {leading && <th className="dt-th dt-leading" scope="col" style={{ width: leading.width || 40 }}>{leading.header}</th>}
              {shown.map((column) => <SortableHeader column={column} key={column.key} onSort={sortBy} sort={sort} />)}
              {actions && (
                <th className="dt-th dt-actions" scope="col" style={{ width: actions.width || 96 }}>
                  {actions.header || <span className="dt-sr">{actions.label || 'Actions'}</span>}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {!sorted.length && empty !== undefined && (
              <tr className="dt-empty"><td colSpan={colCount}>{empty}</td></tr>
            )}
            {sorted.map((row, index) => {
              const extra = rowProps ? rowProps(row, index) || {} : {};
              const key = keyOf(row, index);
              return (
                <React.Fragment key={key}>
                  <tr {...extra}>
                    {leading && <td className="dt-leading">{leading.render(row, index)}</td>}
                    {shown.map((column) => <Cell column={column} index={index} key={column.key} row={row} />)}
                    {actions && <td className="dt-actions"><div className="dt-actions-box">{actions.render(row, index)}</div></td>}
                  </tr>
                  {after && after(row, { colCount, index })}
                </React.Fragment>
              );
            })}
          </tbody>
          {footer && <tfoot>{footer({ colCount, shown, show: choice.show })}</tfoot>}
        </table>
      </div>
    </div>
  );
}

DataTable.propTypes = {
  actions: PropTypes.shape({ header: PropTypes.node, label: PropTypes.string, render: PropTypes.func.isRequired, width: PropTypes.number }),
  after: PropTypes.func,
  ariaLabel: PropTypes.string,
  // A <caption> element (the caller's, with its class).
  caption: PropTypes.node,
  className: PropTypes.string,
  columnState: PropTypes.object,
  columns: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  columnsMenu: PropTypes.oneOf(['auto', false]),
  defaultSort: PropTypes.object,
  empty: PropTypes.node,
  footer: PropTypes.func,
  leading: PropTypes.shape({ header: PropTypes.node, render: PropTypes.func.isRequired, width: PropTypes.number }),
  minWidth: PropTypes.number,
  onSort: PropTypes.func,
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  rowProps: PropTypes.func,
  rows: PropTypes.array.isRequired,
  scrollLabel: PropTypes.string,
  serverSort: PropTypes.bool,
  sort: PropTypes.object,
  storageKey: PropTypes.string,
  testId: PropTypes.string,
  toolbar: PropTypes.node,
  wrapClassName: PropTypes.string,
};
